/**
 * OnboardingChat.js — AI-powered onboarding chat page
 * URL: /get-started
 *
 * Full-screen chat interface that collects family profile data via a
 * structured conversation, saves to Firestore, fires webhooks to Brainstorm,
 * and offers a 30-day free trial at the end.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Fade,
  Alert,
} from '@mui/material';
import {
  Send,
  ArrowForward,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import {
  STEPS,
  getStep,
  applyToProfile,
  buildInitialProfile,
  buildOfferMessage,
} from '../utils/chatFlow';
import { generateFamilyPlan } from '../utils/chartGenerator';

// ─── Constants ──────────────────────────────────────────────────────────────
const EARN_E_AVATAR = '🤖';
const TYPING_DELAY_MS = 800;
const WEBHOOK_URL = 'https://brainstormaikit.com/api/webhooks/earne';

// ─── Message types ──────────────────────────────────────────────────────────
function makeEarneMessage(content, quickReplies = [], id = Date.now()) {
  return { id, role: 'earne', content, quickReplies, timestamp: new Date() };
}
function makeUserMessage(content, id = Date.now()) {
  return { id, role: 'user', content, timestamp: new Date() };
}

// ─── Component ───────────────────────────────────────────────────────────────
export default function OnboardingChat() {
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentStepId, setCurrentStepId] = useState(STEPS.GREETING);
  const [profile, setProfile] = useState(buildInitialProfile());
  const [emailError, setEmailError] = useState('');
  const [signupCode, setSignupCode] = useState('');
  const [saveError] = useState('');
  // Multi-select state
  const [selectedItems, setSelectedItems] = useState([]);
  // Screen time custom input toggle
  const [showCustomScreenTime, setShowCustomScreenTime] = useState(false);
  // Promo tracking state
  const [isDoingPromo, setIsDoingPromo] = useState(false);
  const [promoAsked, setPromoAsked] = useState(false);

  // ─── Scroll to bottom ───────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // ─── Initialise: show promo question first, then greeting ────────────────
  useEffect(() => {
    setTimeout(() => {
      // Ask promo question before the normal flow
      addEarneMessage(
        "Hey! Quick question — are you here for the **'Try All 3 & Get 3 Months Free'** promo? 🎁",
        ['Yes, doing the promo!', 'No, just checking this out']
      );
      setPromoAsked(true);
    }, 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const addEarneMessage = useCallback((content, quickReplies = []) => {
    setMessages(prev => [...prev, makeEarneMessage(content, quickReplies)]);
  }, []);

  const showTyping = useCallback(() => {
    setIsTyping(true);
    return new Promise(resolve => setTimeout(() => {
      setIsTyping(false);
      resolve();
    }, TYPING_DELAY_MS));
  }, []);

  // ─── Process user input for a given step ─────────────────────────────────
  const processStep = useCallback(async (stepId, userInput, currentProfile) => {
    const step = getStep(stepId);
    if (!step) return currentProfile;

    let updatedProfile = { ...currentProfile };

    // Validate + parse
    if (step.validate && !step.validate(userInput)) {
      await showTyping();
      if (stepId === STEPS.EMAIL) {
        addEarneMessage(
          "Hmm, that doesn't look like a valid email. Could you double-check it? 📧",
          []
        );
        setEmailError('Please enter a valid email address');
      } else {
        addEarneMessage("I didn't quite get that — could you try again? 😊", step.quickReplies || []);
      }
      return updatedProfile;
    }

    setEmailError('');

    if (step.field && step.parse) {
      const parsed = step.parse(userInput);
      updatedProfile = applyToProfile(updatedProfile, step.field, parsed);
      setProfile(updatedProfile);
    }

    // Advance to next step
    const nextStepId = step.next;
    if (!nextStepId) return updatedProfile;

    if (nextStepId === STEPS.GENERATING) {
      // Email captured — save to Firestore and fire webhook
      await handleEmailCaptured(updatedProfile);
      return updatedProfile;
    }

    if (nextStepId === STEPS.OFFER) {
      // This is handled by handleEmailCaptured
      return updatedProfile;
    }

    const nextStep = getStep(nextStepId);
    if (!nextStep) return updatedProfile;

    // Build the next message
    let nextMsg;
    if (nextStep.buildNextMessage) {
      nextMsg = nextStep.buildNextMessage(updatedProfile);
    } else if (nextStep.message) {
      nextMsg = nextStep.message;
    }

    if (nextMsg) {
      await showTyping();
      addEarneMessage(nextMsg, nextStep.quickReplies || []);
    }

    setCurrentStepId(nextStepId);
    setSelectedItems([]); // reset multi-select on step change
    setShowCustomScreenTime(false);
    return updatedProfile;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addEarneMessage, showTyping]);

  // ─── Handle email capture + resource generation ──────────────────────────
  const handleEmailCaptured = useCallback(async (capturedProfile) => {
    // Show generating message
    await showTyping();
    const generatingStep = getStep(STEPS.GENERATING);
    addEarneMessage(generatingStep.message, []);
    setCurrentStepId(STEPS.GENERATING);

    try {
      // Generate the plan (client-side for speed)
      const code = generateSignupCode();
      const profileWithCode = { ...capturedProfile, signup_code: code };
      setSignupCode(code);

      const plan = generateFamilyPlan(profileWithCode);
      // plan generated (used for email send below)

      // Save to Firestore with 5-second timeout (non-blocking for UX)
      let docId = null;
      try {
        const firestoreTimeout = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Firestore timeout after 5s')), 5000)
        );
        const docRef = await Promise.race([
          addDoc(collection(db, 'lead_profiles'), {
            ...profileWithCode,
            created_at: new Date(),
          }),
          firestoreTimeout,
        ]);
        docId = docRef.id;
        console.log('Lead saved to Firestore:', docId);
      } catch (err) {
        console.warn('Firestore save failed (non-critical):', err.message);
        // Don't block the flow — proceed anyway
      }

      // Fire webhook to Brainstorm (non-blocking)
      fireWebhook('lead.created', {
        email: capturedProfile.email,
        signup_code: code,
        doc_id: docId,
        family: {
          kids: (capturedProfile.kids || []).length,
          ages: (capturedProfile.kids || []).map(k => k.age).filter(Boolean),
        },
        challenges: capturedProfile.challenges?.pain_points || [],
        goals: capturedProfile.goals,
        created_at: new Date().toISOString(),
      }).catch(err => console.warn('Webhook failed (non-critical):', err));

      // Call Firebase Function to send the email
      sendResourcesEmail(capturedProfile, plan, code).catch(err =>
        console.warn('Email send failed (non-critical):', err)
      );

      // Show the offer after a moment
      await new Promise(r => setTimeout(r, 1200));
      const offerMsg = buildOfferMessage(capturedProfile);
      await showTyping();
      addEarneMessage(offerMsg, []);
      setCurrentStepId(STEPS.OFFER);
      setProfile({ ...profileWithCode, signup_code: code });

    } catch (err) {
      console.error('Error in generation step:', err);
      await showTyping();
      addEarneMessage(
        "I ran into a small hiccup, but don't worry — your profile is saved! Check your email for your resources. 📧",
        []
      );
    }
  }, [addEarneMessage, showTyping]);

  // ─── Send message handler ────────────────────────────────────────────────
  const handleSend = useCallback(async (text) => {
    const trimmed = (text || inputValue).trim();
    if (!trimmed || isTyping || currentStepId === STEPS.GENERATING || currentStepId === STEPS.DONE) return;

    setInputValue('');
    setMessages(prev => [...prev, makeUserMessage(trimmed)]);

    // ── Handle promo question answer (before normal flow) ────────────────
    if (promoAsked && currentStepId === STEPS.GREETING) {
      const lowerTrimmed = trimmed.toLowerCase();
      const doingPromo = lowerTrimmed.includes('yes') ||
        lowerTrimmed.includes('promo') ||
        lowerTrimmed.includes('3 months') ||
        trimmed === 'Yes, doing the promo!';

      setIsDoingPromo(doingPromo);

      // Record promo status in profile
      setProfile(prev => ({ ...prev, is_doing_promo: doingPromo }));

      await showTyping();

      if (doingPromo) {
        addEarneMessage(
          "Amazing! 🎉 You're doing the full promo — that means you get 90 days FREE when you finish all 3 experiences and submit feedback. Let's make this count!\n\nNow, let's build your family's chore system. How many kids do you have?",
          ['1', '2', '3', '4', '5+']
        );
      } else {
        addEarneMessage(
          "No worries! Let's still build you an awesome chore system. How many kids do you have?",
          ['1', '2', '3', '4', '5+']
        );
      }

      setCurrentStepId(STEPS.NUM_KIDS);
      return;
    }

    await processStep(currentStepId, trimmed, profile);
  }, [inputValue, isTyping, currentStepId, profile, promoAsked, processStep, addEarneMessage, showTyping]);

  // ─── Multi-select step helper ────────────────────────────────────────────
  const isMultiSelectStep = useCallback((stepId) => {
    const step = getStep(stepId);
    return !!(step && step.allowMultiple);
  }, []);

  // ─── Submit multi-select selections ─────────────────────────────────────
  const handleMultiSelectContinue = useCallback(async () => {
    if (selectedItems.length === 0 || isTyping) return;
    const combined = selectedItems.join(', ');
    setSelectedItems([]);
    setMessages(prev => [...prev, makeUserMessage(combined)]);
    await processStep(currentStepId, combined, profile);
  }, [selectedItems, isTyping, currentStepId, profile, processStep]);

  // ─── Quick reply handler ─────────────────────────────────────────────────
  const handleQuickReply = useCallback((text) => {
    if (isTyping || currentStepId === STEPS.GENERATING || currentStepId === STEPS.DONE) return;
    // "Other (tell us your rules)" — show custom text input
    if (text === '✏️ Other (tell us your rules)') {
      setShowCustomScreenTime(true);
      return;
    }
    if (isMultiSelectStep(currentStepId)) {
      // Toggle selection
      setSelectedItems(prev =>
        prev.includes(text) ? prev.filter(i => i !== text) : [...prev, text]
      );
    } else {
      handleSend(text);
    }
  }, [handleSend, isTyping, currentStepId, isMultiSelectStep]);

  // ─── Signup CTA handler ──────────────────────────────────────────────────
  const handleSignup = useCallback(() => {
    const code = signupCode || profile.signup_code || '';
    const email = encodeURIComponent(profile.email || '');
    navigate(`/signup?code=${code}&email=${email}`);
  }, [navigate, signupCode, profile]);

  // ─── Key press ───────────────────────────────────────────────────────────
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          boxShadow: 3,
          flexShrink: 0,
        }}
      >
        <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', fontSize: '1.4rem' }}>
          {EARN_E_AVATAR}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" component="h1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            Earn-E
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85, display: 'block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {isDoingPromo ? '🎁 Promo: 90 Days Free — Building your custom plan' : 'AI Family Assistant — Building your custom plan'}
          </Typography>
        </Box>
        <Box sx={{ ml: 1, display: 'flex', gap: 1, flexShrink: 0 }}>
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/signup')}
            sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', minHeight: 48 }}
          >
            Skip to Signup
          </Button>
        </Box>
      </Box>

      {/* Messages area */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: { xs: 2, md: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          maxWidth: 800,
          mx: 'auto',
          alignSelf: 'center',
          width: '100%',
        }}
      >
        {messages.map((msg, i) => (
          <Fade in key={msg.id} timeout={300}>
            <Box>
              <MessageBubble
                message={msg}
                onQuickReply={handleQuickReply}
                isLastEarneMessage={
                  msg.role === 'earne' &&
                  messages.slice(i + 1).every(m => m.role !== 'earne')
                }
                multiSelectStep={isMultiSelectStep(currentStepId)}
                selectedItems={selectedItems}
              />
              {/* Show signup/offer buttons after OFFER step */}
              {msg.role === 'earne' &&
                currentStepId === STEPS.OFFER &&
                i === messages.length - 1 && (
                  <OfferButtons
                    onSignup={handleSignup}
                    onSkip={() => navigate('/signup')}
                    signupCode={signupCode}
                  />
                )}
            </Box>
          </Fade>
        ))}

        {/* Typing indicator */}
        {isTyping && <TypingIndicator />}

        {/* Save error alert */}
        {saveError && (
          <Alert severity="warning" sx={{ mt: 1, borderRadius: 2 }}>
            {saveError}
          </Alert>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input area */}
      {currentStepId !== STEPS.DONE && currentStepId !== STEPS.OFFER && (
        <Box
          sx={{
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            p: 2,
            maxWidth: 800,
            mx: 'auto',
            alignSelf: 'center',
            width: '100%',
          }}
        >
          {/* Screen time custom input */}
          {currentStepId === STEPS.ALLOWANCE_BUDGET && showCustomScreenTime && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Describe your screen time rules:
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75, fontStyle: 'italic' }}>
                e.g. "No screens on school nights, 2hrs on weekends, phone off at 8pm"
              </Typography>
            </Box>
          )}

          {/* Allowance budget quick-select presets */}
          {currentStepId === STEPS.ALLOWANCE_SYSTEM && (
            <Box sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                Quick select (weekly total):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {[
                  { label: '$20/wk ($5/kid×4)', value: '$20/week' },
                  { label: '$40/wk ($10/kid)', value: '$40/week' },
                  { label: '$60/wk ($15/kid)', value: '$60/week' },
                  { label: '$80/wk ($20/kid)', value: '$80/week' },
                  { label: 'Skip for now', value: 'skip' },
                ].map(opt => (
                  <Chip
                    key={opt.value}
                    label={opt.label}
                    onClick={() => handleSend(opt.value)}
                    variant="outlined"
                    sx={{
                      cursor: 'pointer',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      height: 44,
                      fontSize: '0.875rem',
                      '&:hover': { bgcolor: 'primary.main', color: 'white' },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Multi-select continue button */}
          {isMultiSelectStep(currentStepId) && selectedItems.length > 0 && (
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                Selected: {selectedItems.join(', ')}
              </Typography>
              <Button
                variant="contained"
                size="small"
                onClick={handleMultiSelectContinue}
                disabled={isTyping}
                endIcon={<ArrowForward />}
                sx={{ borderRadius: 3, ml: 'auto' }}
              >
                Continue ({selectedItems.length} selected)
              </Button>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            <TextField
              ref={inputRef}
              fullWidth
              multiline
              maxRows={3}
              value={inputValue}
              onChange={e => {
                setInputValue(e.target.value);
                setEmailError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder={getInputPlaceholder(currentStepId)}
              variant="outlined"
              disabled={isTyping || currentStepId === STEPS.GENERATING}
              error={!!emailError}
              helperText={emailError || (currentStepId === STEPS.EMAIL ? 'Works with gmail, yahoo, outlook — any email provider' : '')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                },
                '& .MuiInputBase-input': {
                  minHeight: 48,
                },
              }}
              inputProps={{
                type: currentStepId === STEPS.EMAIL ? 'email' : 'text',
                inputMode: currentStepId === STEPS.EMAIL ? 'email' : 'text',
                autoCapitalize: currentStepId === STEPS.EMAIL ? 'none' : 'sentences',
                autoCorrect: currentStepId === STEPS.EMAIL ? 'off' : 'on',
              }}
            />
            <IconButton
              onClick={() => handleSend()}
              disabled={!inputValue.trim() || isTyping || currentStepId === STEPS.GENERATING}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                width: 44,
                height: 44,
                flexShrink: 0,
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
              }}
            >
              {currentStepId === STEPS.GENERATING ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Send fontSize="small" />
              )}
            </IconButton>
          </Box>

          {/* Step progress */}
          <StepProgress currentStepId={currentStepId} />
        </Box>
      )}
    </Box>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function MessageBubble({ message, onQuickReply, isLastEarneMessage, multiSelectStep, selectedItems }) {
  const isEarne = message.role === 'earne';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isEarne ? 'row' : 'row-reverse',
        gap: 1.5,
        alignItems: 'flex-start',
      }}
    >
      {isEarne && (
        <Avatar
          sx={{
            bgcolor: 'primary.main',
            width: 36,
            height: 36,
            fontSize: '1.1rem',
            flexShrink: 0,
            mt: 0.5,
          }}
        >
          🤖
        </Avatar>
      )}

      <Box sx={{ maxWidth: { xs: '85%', md: '70%' }, minWidth: 60 }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            borderRadius: isEarne ? '4px 16px 16px 16px' : '16px 4px 16px 16px',
            bgcolor: isEarne ? 'grey.100' : 'primary.main',
            color: isEarne ? 'text.primary' : 'white',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              overflowWrap: 'break-word',
              '& strong, & b': { fontWeight: 700 },
            }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
          />
        </Paper>

        {/* Timestamp */}
        <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5, mt: 0.25, display: 'block' }}>
          {message.timestamp?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </Typography>

        {/* Quick replies — only show for most recent Earn-E message */}
        {isEarne && isLastEarneMessage && message.quickReplies?.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1, maxWidth: '100%' }}>
            {message.quickReplies.map(reply => {
              const isSelected = selectedItems && selectedItems.includes(reply);
              return (
                <Chip
                  key={reply}
                  label={reply}
                  onClick={() => onQuickReply(reply)}
                  variant={isSelected ? 'filled' : 'outlined'}
                  icon={multiSelectStep && isSelected ? <CheckCircle fontSize="small" /> : undefined}
                  sx={{
                    cursor: 'pointer',
                    borderColor: 'primary.main',
                    color: isSelected ? 'white' : 'primary.main',
                    bgcolor: isSelected ? 'primary.main' : 'transparent',
                    height: 44,
                    fontSize: '0.9rem',
                    '&:hover': {
                      bgcolor: isSelected ? 'primary.dark' : 'primary.main',
                      color: 'white',
                    },
                    transition: 'all 0.15s',
                    maxWidth: '100%',
                    padding: '0 8px',
                  }}
                />
              );
            })}
          </Box>
        )}
        {/* Multi-select hint */}
        {isEarne && isLastEarneMessage && multiSelectStep && message.quickReplies?.length > 0 && (
          <Typography variant="caption" sx={{ color: 'text.secondary', px: 0.5, mt: 0.5, display: 'block', fontStyle: 'italic' }}>
            Select all that apply, then tap Continue ↓
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function TypingIndicator() {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 0.5 }}>
      <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36, fontSize: '1.1rem' }}>
        🤖
      </Avatar>
      <Paper
        elevation={0}
        sx={{
          px: 2,
          py: 1.5,
          borderRadius: '4px 16px 16px 16px',
          bgcolor: 'grey.100',
          display: 'flex',
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        {[0, 1, 2].map(i => (
          <Box
            key={i}
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'text.secondary',
              animation: 'bounce 1.2s infinite',
              animationDelay: `${i * 0.2}s`,
              '@keyframes bounce': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-5px)' },
              },
            }}
          />
        ))}
      </Paper>
    </Box>
  );
}

function OfferButtons({ onSignup, onSkip, signupCode }) {
  return (
    <Box
      sx={{
        mt: 2,
        p: 2,
        bgcolor: 'primary.main',
        borderRadius: 3,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        mx: { xs: 0, md: 5 },
      }}
    >
      <Typography variant="body2" sx={{ color: 'white', textAlign: 'center', fontWeight: 600 }}>
        🎁 Your code: <strong>{signupCode}</strong>
      </Typography>
      <Button
        variant="contained"
        size="large"
        fullWidth
        onClick={onSignup}
        endIcon={<ArrowForward />}
        sx={{
          bgcolor: 'white',
          color: 'primary.main',
          fontWeight: 700,
          fontSize: '1rem',
          borderRadius: 2,
          '&:hover': { bgcolor: 'grey.100' },
        }}
      >
        Yes! Set Me Up — 30 Days Free
      </Button>
      <Button
        variant="text"
        size="small"
        fullWidth
        onClick={onSkip}
        sx={{ 
          color: 'rgba(255,255,255,0.8)',
          minHeight: 48,
          minWidth: 48,
          py: 1.5
        }}
      >
        Skip for now — go to regular signup
      </Button>
    </Box>
  );
}

function StepProgress({ currentStepId }) {
  const orderedSteps = [
    STEPS.NUM_KIDS,
    STEPS.KID_NAMES,
    STEPS.CHALLENGES,
    STEPS.CHORE_TYPES,
    STEPS.CHORE_SCHEDULE,
    STEPS.ALLOWANCE_SYSTEM,
    STEPS.ALLOWANCE_BUDGET,
    STEPS.SCREEN_TIME,
    STEPS.GOALS_30,
    STEPS.GOALS_90,
    STEPS.EMAIL,
    STEPS.GENERATING,
  ];
  const total = orderedSteps.length;
  const current = orderedSteps.indexOf(currentStepId) + 1;
  const pct = current > 0 ? Math.round((current / total) * 100) : 5;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Building your plan...
        </Typography>
        <Typography variant="caption" color="primary">
          {Math.min(pct, 95)}%
        </Typography>
      </Box>
      <Box
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: 'grey.200',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            height: '100%',
            borderRadius: 2,
            bgcolor: 'primary.main',
            width: `${Math.min(pct, 95)}%`,
            transition: 'width 0.4s ease',
          }}
        />
      </Box>
    </Box>
  );
}

// ─── Utilities ────────────────────────────────────────────────────────────────

function getInputPlaceholder(stepId) {
  switch (stepId) {
    case STEPS.NUM_KIDS: return 'Type a number, e.g. "3"';
    case STEPS.KID_NAMES: return 'e.g. "Emma 8, Liam 12, Noah 5"';
    case STEPS.EMAIL: return 'your@email.com';
    case STEPS.CHALLENGES: return 'Type your challenges or pick above...';
    case STEPS.CHORE_TYPES: return 'Pick options above or describe your own...';
    default: return 'Type your reply...';
  }
}

function renderMarkdown(text) {
  if (!text) return '';
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}

function generateSignupCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

async function fireWebhook(event, data) {
  try {
    const payload = {
      event,
      timestamp: new Date().toISOString(),
      data,
    };
    const res = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      console.warn(`Webhook ${event} returned ${res.status}`);
    }
  } catch (err) {
    console.warn(`Webhook ${event} failed:`, err.message);
    // Non-critical — swallow
  }
}

async function sendResourcesEmail(profile, plan, signupCode) {
  // Call Firebase Function to send the email
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const { app } = await import('../firebase');
    const functions = getFunctions(app, 'us-central1');
    const sendEmail = httpsCallable(functions, 'sendOnboardingEmail');
    await sendEmail({ profile, plan, signupCode });
  } catch (err) {
    console.warn('Could not send resources email:', err.message);
  }
}
